/**
 * ============================================
 * LOGIN PAGE COMPONENT
 * ============================================
 * 
 * Login page with Cognito OAuth redirect.
 * Shows errors if authentication fails.
 */


import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { appMetadata } from '@/config';
import { LogIn, Shield, AlertCircle } from 'lucide-react';

export function LoginPage() {
  const { login, isLoading, error } = useAuth();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {/* Logo */}
          <div className="mx-auto h-16 w-16 rounded-xl bg-primary flex items-center justify-center mb-4">
            <img src="icons/lokko.png" alt="" />
          </div>
          
          <CardTitle className="text-2xl">{appMetadata.name}</CardTitle>
          <CardDescription>
            Sign in to access the server monitoring dashboard
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Authentication Error</p>
                <p className="text-xs text-destructive/80 mt-1">{error}</p>
              </div>
            </div>
          )}
          
          <Button
            className="w-full"
            size="lg"
            onClick={login}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <>
                <LogIn className="h-5 w-5 mr-2" />
                Sign in with Cognito
              </>
            )}
          </Button>
          
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Secured by AWS Cognito</span>
          </div>
          
          <p className="text-xs text-center text-muted-foreground mt-4">
            Contact your administrator if you don't have access.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
